import { store } from '@reduxStore';
import { consignmentSlice } from '@api/Trinium/Endpoints/Consignment/slice';
import { NotesGridSaveAction } from '@components/DataGrid/Notes/types';
import { DriverNotesGridSaveAction } from '@components/DataGrid/DriverNotes/types';
import { BaseApiRequest, LegStatus, LegType, ParsedDate, parseIsoDate } from '../../types';
import { getJobCatTransTypeString } from '../Dispatch/types';
import { legSlice } from './slice';
import { Location, mapLocationToLegLoc } from '../Location/types';

/** Abstracting the Raw API Leg fields for reuse between types */
interface LegRawFields {
  id: number;
  no: number;
  type?: keyof typeof LegType;
  status?: keyof typeof LegStatus;
  isActiveLeg?: boolean;
  isHazmat?: boolean;
  isInvoiced?: boolean;
  /** Flag indicating the leg has unapproved driver pay, which the user is asked about when removing the leg */
  hasUnapprovedPay?: boolean;
  isReminder?: boolean;
  reqHazmatCert?: boolean;
  priority?: number;
  schdDelvFrom?: string | null;
  schdDelvTo?: string | null;
  schdPuFrom?: string | null;
  schdPuTo?: string | null;
  hubId?: string | null;
  puDate?: string;
  delvDate?: string;
  cntrId?: string;
  cntrType?: string;
  cntrSize?: string;
  cntrProviderId?: string;
  cntrProviderName?: string;
  chassisId?: string | null;
  isChassisOwned?: boolean;
  chassis?: LegChassis;
  // chassisPoolId?: string;
  chassisProvId?: string;
  fullAvailDate?: string | null;
  emptyAvailDate?: string | null;
  chassisAvailDate?: string | null;
  tractorId?: string | null;
  csnId?: number;
  csn: LegConsignment;
  drvId?: string | null;
  drvNotes?: string;
  notes?: LegNotesRaw[];
  drv?: LegDriver;
  jobId: number;
  job?: LegJob;
  puId?: string | null;
  pu?: LegLocationRaw;
  puDelvReceiptSent?: boolean;
  puSealNo?: string;
  dehireId?: string;
  delvId?: string | null;
  delv?: LegLocationRaw;
  pcs?: number | null;
  wgtLb?: number | null;
  relNo?: string;
  custId?: string;
  cust?: LegCustomer;
  ediRefNo?: string;
  streetTurnDate?: string | null;
  miles?: number | null;
  houseBol?: string;
  puRefNo?: string;
}

/** API Leg object */
export type LegRaw = Record<
  string,
  | number
  | boolean
  | string
  | LegJob
  | LegConsignment
  | LegDriver
  | LegLocation
  | LegCustomer
  | LegChassis
  | LegNotesRaw
  | LegType
  | LegStatus
  | null
  | undefined
> &
  LegRawFields;

interface LegJob {
  cat: string;
  catTransType: string;
  bookingRefNo: string;
  docDate?: string;
  vslName?: string;
  voyage?: string;
  frstRcvDate?: string;
}

interface LegConsignment {
  no: number;
  cutoffDate?: string;
  lastFreeDate?: string;
  isWorkflow: boolean;
  isBulk: boolean;
  isLive: boolean;
  isOverweight: boolean;
  isRailBilled: boolean;
  puRefNo?: string;
  delvRefNo?: string;
  masterBol?: string;
  custRefNo?: string;
}

interface LegCustomer {
  name?: string;
  emailAddr?: string;
  phone?: string;
}

export interface LegDriver {
  name?: string;
  type: 'B' | 'D';
  emailAddr?: string;
  cellPhone?: string;
}

interface LegLocationRaw {
  name?: string;
  addr1?: string;
  addr2?: string;
  cityId?: string;
  city?: string;
  state?: string;
  zip?: string;
  cntryId?: string;
  cntryName?: string;
  areaName?: string;
  phone?: string;
  fax?: string;
  emailAddr?: string;
  notes?: string;
  puNotes?: string;
  delvNotes?: string;
  usePuNotes?: boolean;
  useDelvNotes?: boolean;
}

export type LegLocation = LegLocationRaw & {
  id: string;
};

interface LegChassis {
  // poolName?: string;
  provName?: string;
}

interface LegNotesRaw {
  usrId?: string;
  usrName?: string;
  date?: string;
  note?: string;
}

export type LegNotes = Omit<LegNotesRaw, 'date'> & {
  /** DataGrid needs something unique and we arent getting an id */
  index: number;
  timestamp?: ParsedDate | null;
};

/** Fields to exclude when converting the Raw API Leg object to the UI Leg object */
type ExcludedRawLegFields =
  | 'schdDelvFrom'
  | 'schdDelvTo'
  | 'delvDate'
  | 'delv'
  | 'schdPuFrom'
  | 'schdPuTo'
  | 'puDate'
  | 'pu'
  | 'fullAvailDate'
  | 'emptyAvailDate'
  | 'chassisAvailDate'
  | 'streetTurnDate';

/** Fields to exclude when mutating a Leg object */
export type LegReadOnlyFields = 'job' | 'csn' | 'cust' | 'drv' | 'pu' | 'delv' | 'chassis' | 'notes';

/**
 * Mutable properties of a Leg
 */
export type MutatableLegRaw = Partial<Omit<LegRawFields, LegReadOnlyFields>> & {
  /** Last Free Date and cutoff date are consignment properties that are updated through the Leg PUT API */
  lastFreeDate?: string | null;
  cutoffDate?: string | null;
};

/** UI Leg object */
export type Leg = Record<
  string,
  string | number | boolean | LegConsignment | LegDriver | LegStatus | LegType | LegNotes[] | ParsedDate | undefined | null
> &
  Omit<LegRawFields, ExcludedRawLegFields> & {
    priority: number;
    scheduledDeliveryDateFrom?: ParsedDate;
    scheduledDeliveryDateTo?: ParsedDate;
    delvDate?: ParsedDate;
    deliveryLocation?: LegLocation;
    scheduledPickupDateFrom?: ParsedDate;
    scheduledPickupDateTo?: ParsedDate;
    pickupDate?: ParsedDate;
    pickupLocation?: LegLocation;
    fullAvailableDate?: ParsedDate;
    emptyAvailableDate?: ParsedDate;
    chassisAvailableDate?: ParsedDate;
    streetTurnDate?: ParsedDate;
    cutoffDate?: ParsedDate;
    lastFreeDate?: ParsedDate;
    notes?: LegNotes[];
    firstReceiveDate?: ParsedDate;
  };

/** Method for transforming API Leg object to a UI Leg object */
export function transformLegResponse({
  schdDelvFrom,
  schdDelvTo,
  delvDate,
  delvId,
  delv,
  schdPuFrom,
  schdPuTo,
  puDate,
  puId,
  pu,
  fullAvailDate,
  emptyAvailDate,
  chassisAvailDate,
  streetTurnDate,
  ...result
}: LegRaw) {
  return {
    priority: result.priority ?? 2,
    drvNotes: result.drvNotes ?? undefined,
    scheduledDeliveryDateFrom: parseIsoDate(schdDelvFrom),
    scheduledDeliveryDateTo: parseIsoDate(schdDelvTo),
    delvDate: parseIsoDate(delvDate),
    delvId: delvId,
    deliveryLocation: delvId ? { id: delvId, ...delv } : null,
    scheduledPickupDateFrom: parseIsoDate(schdPuFrom),
    scheduledPickupDateTo: parseIsoDate(schdPuTo),
    puId: puId,
    pickupDate: parseIsoDate(puDate),
    pickupLocation: puId ? { id: puId, ...pu } : null,
    fullAvailableDate: parseIsoDate(fullAvailDate),
    emptyAvailableDate: parseIsoDate(emptyAvailDate),
    chassisAvailableDate: parseIsoDate(chassisAvailDate),
    streetTurnDate: parseIsoDate(streetTurnDate),
    cutoffDate: parseIsoDate(result.csn.cutoffDate),
    lastFreeDate: parseIsoDate(result.csn.lastFreeDate),
    firstReceiveDate: parseIsoDate(result.job?.frstRcvDate),

    ...result,
    /** API omits false booleans, so default it here or cache merges can't clear a stale true on driver reassignment */
    puDelvReceiptSent: result.puDelvReceiptSent ?? false,
    job: {
      ...result.job,
      catTransType: getJobCatTransTypeString(result.job?.catTransType),
      frstRcvDate: parseIsoDate(result.job?.frstRcvDate)
    },
    csn: {
      ...result.csn
    },
    cust: {
      ...result.cust
    },
    drv: result.drv
      ? {
          ...result.drv
        }
      : undefined,
    notes: result.notes
      ? [
          ...result.notes.map(({ date, ...note }, index) => ({
            ...note,
            index: index,
            timestamp: parseIsoDate(date)
          }))
        ]
      : []
  } as Leg;
}
export function transformLegsResponse(result: LegRaw[]): Leg[] {
  return result.map((item) => transformLegResponse(item));
}

/**
 * Response shape for the putLeg mutation. Most actions return a raw `Leg[]` from the API; the `assignResources`
 * action additionally returns a `canApprove` flag wrapped alongside the legs so the UI can re-enable the
 * Approve button without refetching the consignment.
 */
export interface PutLegResponse {
  legs: Leg[];
  canApprove?: boolean;
}

/** Raw API response from assignResources - legs are wrapped together with the consignment's canApprove flag */
export interface PutLegAssignResourcesRawResponse {
  legs: LegRaw[];
  canApprove: boolean;
}

export interface DeleteLegRequest extends BaseApiRequest {
  /** Leg Identifier */
  id: number;
  /** Consignment Identifier required to sync the cache */
  csnId: number;
  /** Job Identifier optionally needed to sync the cache */
  jobId?: number;
  /** Answer to deleting the leg's unapproved driver pay (see resolveDeleteDriverPayments). Omitted when the leg has none. */
  deleteDriverPayments?: boolean;
}

export interface PutLegsRequest extends BaseApiRequest {
  /** PUT Action */
  actionType: PutLegsAction;
}

/** Either collection may be omitted by the API when it is empty (all legs succeeded, or all were rejected) */
export interface PutLegsRawResponse {
  legs?: LegRaw[];
  errors?: BatchLegError[];
}

export interface PutLegsResponse {
  legs: Leg[];
  errors: BatchLegError[];
}

export interface BatchLegError {
  legId: number;
  errorMessage: string;
}

/** Either collection may be omitted by the API when it is empty (all legs succeeded, or all were rejected) */
export interface PatchLegsRawResponse {
  legs?: LegRaw[];
  errors?: BatchLegError[];
}

export interface PatchLegsResponse {
  legs: Leg[];
  errors: BatchLegError[];
}

export interface PatchLegsRequest extends BaseApiRequest {
  legIds: number[];
  leg:
    | Pick<PatchLegPayload, 'chassisId' | 'cpyCntrChassisToAllLegs'>
    | Pick<PatchLegPayload, 'delvId'>
    | Pick<PatchLegPayload, 'puId'>
    | Pick<PatchLegPayload, 'puSealNo'>
    | Pick<PatchLegPayload, 'relNo'>
    | Pick<PatchLegPayload, 'schdDelvFrom' | 'schdDelvTo'>
    | Pick<PatchLegPayload, 'schdPuFrom' | 'schdPuTo'>
    | Pick<PatchLegPayload, 'cntrId' | 'cntrSize' | 'cntrType' | 'wgtLb' | 'cpyCntrChassisToAllLegs'>;
}

export interface SetScheduleRequest extends BaseApiRequest {
  /** PUT Action */
  actionType: SetScheduleAction;
}

export interface PutLegRequest extends BaseApiRequest {
  /** Leg Identifier */
  id: number;
  /** PUT Action */
  actionType: PutLegAction;
  /** When true, skip updating the fetchLeg and consignment API caches in onQueryStarted. Used to prevent intermediate re-renders when multiple mutations are batched. */
  skipCacheUpdate?: boolean;
}

/**
 * Helper properties required for updating the cache. Mainly required for embedded objects where we only need to update the ID but
 * in order to correctly update the cache require additional fields.
 */
export interface PatchLegOptimisticExtensions {
  optimisticDelvLoc?: LegLocation;
  optimisticPuLoc?: LegLocation;
}

export interface PatchLegRequest extends BaseApiRequest {
  leg: PatchLegPayload;
  optimistic?: boolean;
  /** Helper properties to update the cache optimistically */
  optimisticExtensions?: PatchLegOptimisticExtensions;
  /** When true, skip updating the fetchLeg and consignment API caches in onQueryStarted. Used to prevent intermediate re-renders when multiple mutations are batched. */
  skipCacheUpdate?: boolean;
}

export type BatchLegRequest = PatchLegPayload & {
  optimisticProperties?: PatchLegOptimisticExtensions;
};

export type PatchLegPayload = Pick<LegRaw, 'id'> &
  MutatableLegRaw & {
    /** Whether a container/chassis change should be copied to all applicable legs and the order, determined by the Web UI before the request is sent (see resolveCopyCntrChassisToAllLegs) */
    cpyCntrChassisToAllLegs?: boolean;
  };

export type PutLegsAction =
  | AssignResourcesAction
  | DeliverAction
  | PickupAction
  | PickupAndDeliverAction
  | SetAvailabilityLegsAction
  | UndoAction
  | HubContainerAction;

export type PutLegAction =
  | AddDispatchNoteAction
  | DriverNoteAction
  | AssignResourceAction
  | RunTraceAction
  | SetAvailabilityAction
  | SetOnSiteAction
  | UndoStatusAction;

export interface SetScheduleData {
  legIds: number[];
  schdPuFrom?: string | null;
  schdPuTo?: string | null;
  schdDelvFrom?: string | null;
  schdDelvTo?: string | null;
  removeSchdPuTimes?: boolean;
  removeSchdPuDate?: boolean;
  removeSchdDelvTimes?: boolean;
  removeSchdDelvDate?: boolean;
}

interface SetScheduleAction {
  data: SetScheduleData;
}

interface AssignResourceAction {
  action: 'assignResources';
  data: {
    drvId: string;
    tractorId?: string;
  };
}

interface AssignResourcesAction {
  action: 'assignResources';
  data: AssignResourceAction['data'] & {
    legIds: number[];
  };
}

interface DeliverAction {
  action: 'deliver';
  data: {
    legIds: number[];
    deliveryDate: string;
  };
}

interface PickupAction {
  action: 'pickup';
  data: {
    legIds: number[];
    pickupDate: string;
  };
}

interface PickupAndDeliverAction {
  action: 'pickupAndDeliver';
  data: {
    legIds: number[];
    statusDate: string;
  };
}

interface SetAvailabilityLegsAction {
  action: 'setAvailability';
  data: SetAvailabilityAction['data'] & {
    legIds: number[];
  };
}

interface UndoAction {
  action: 'undo';
  data: {
    legIds: number[];
  };
}

interface HubContainerAction {
  action: 'hubContainer';
  data: {
    hubId: string;
    legIds: number[];
  };
}
interface SetAvailabilityAction {
  action: 'setAvailability';
  data: {
    availDate?: string | null;
    lastFreeDate?: string | null;
    cutoffDate?: string | null;
    chassisAvailDate?: string | null;
  };
}
interface SetOnSiteAction {
  action: 'setOnSite';
  data?: undefined;
}

interface RunTraceAction {
  action: 'runTrace';
  data?: undefined;
}

interface UndoStatusAction {
  action: 'undo';
  data?: undefined;
}

interface AddDispatchNoteAction {
  action: NotesGridSaveAction.addDispatchNote;
  data: {
    note: string;
  };
}

interface DriverNoteAction {
  action: DriverNotesGridSaveAction.addDriverNote | DriverNotesGridSaveAction.setDriverNote;
  data: {
    note?: string;
    allContainers?: boolean;
    allLegs?: boolean;
  };
}

const legEndpointName = 'fetchLeg';

/** Method for manually updating the RTK Cached Leg of relevant Leg APIs  */
export function updateLegApiCache(updatedLeg: Pick<Leg, 'id'> & Partial<Leg>) {
  const invalidEndpoints = legSlice.util.selectInvalidatedBy(store.getState(), [{ type: 'legs', id: updatedLeg.id }]);

  return invalidEndpoints.map((currentInvalidEndpoint) => {
    type LegEndpoints = typeof legSlice.endpoints;
    type InvalidatedEndpointType = LegEndpoints['fetchLeg'];

    /** Get the original args as a strongly typed object for the specific endpoint we are handling */
    const originalArgs = currentInvalidEndpoint.originalArgs as InvalidatedEndpointType['Types']['QueryArg'];

    return store.dispatch(
      legSlice.util.updateQueryData(legEndpointName, originalArgs, (draft) => {
        Object.assign(draft, updatedLeg);
      })
    );
  });
}

/** Method for manually removing the RTK Cached Leg of relevant Leg APIs  */
export function removeLegApiCache(legId: number) {
  const invalidEndpoints = legSlice.util.selectInvalidatedBy(store.getState(), [{ type: 'legs', id: legId }]);

  return invalidEndpoints.map((currentInvalidEndpoint) => {
    type LegEndpoints = typeof legSlice.endpoints;
    type InvalidatedEndpointType = LegEndpoints['fetchLeg'];

    /** Get the original args as a strongly typed object for the specific endpoint we are handling */
    const originalArgs = currentInvalidEndpoint.originalArgs as InvalidatedEndpointType['Types']['QueryArg'];

    return store.dispatch(
      legSlice.util.updateQueryData(legEndpointName, originalArgs, (draft) => {
        if (draft.legId === legId) {
          return undefined;
        }

        return draft;
      })
    );
  });
}

export function updateLegConsignmentCache(updatedLeg: Pick<Leg, 'id'> & Partial<Leg>) {
  const invalidEndpoints = consignmentSlice.util.selectInvalidatedBy(store.getState(), [{ type: 'consignment', id: updatedLeg.csnId }]);

  return invalidEndpoints.map((currentInvalidEndpoint) => {
    type ConsignmentEndpoints = typeof consignmentSlice.endpoints;
    const consignmentEndpointName = 'fetchConsignment';
    type InvalidatedEndpointType = ConsignmentEndpoints['fetchConsignment'];

    const originalArgs = currentInvalidEndpoint.originalArgs as InvalidatedEndpointType['Types']['QueryArg'];

    return store.dispatch(
      consignmentSlice.util.updateQueryData(consignmentEndpointName, originalArgs, (draft) => {
        /**
         * Update the Consignment Header values that are copied from the updated leg.
         * Coalesce each field so a blank/absent leg value never wipes an already-populated
         * header value (e.g. saving a leg that inherited these via the same-container copy rule).
         */
        Object.assign(draft, {
          cntrId: updatedLeg.cntrId ?? draft.cntrId,
          cntr: {
            ...draft.cntr,
            pcs: updatedLeg.pcs ?? draft.cntr?.pcs,
            wgtLb: updatedLeg.wgtLb ?? draft.cntr?.wgtLb,
            sealNo: updatedLeg.puSealNo || draft.cntr?.sealNo,
            emptyRelNo: updatedLeg.relNo || draft.cntr?.emptyRelNo
          },
          chassisId: updatedLeg.chassisId ?? draft.chassisId
        });

        /** Update the specific leg within the consignment, or add it if it's newly created (e.g. via hub). */
        const leg = draft.consignmentLegs.find((leg) => leg.id === updatedLeg.id);
        if (leg) {
          Object.assign(leg, updatedLeg);
        } else {
          draft.consignmentLegs.push(updatedLeg as Leg);
          draft.consignmentLegs.sort((a, b) => (a.no > b.no ? 1 : -1));
        }
      })
    );
  });
}

/*
 * Applies a location's saved name and address to the legs of a job that use it, in every cached Consignment for that job.
 * Mirrors Job:UpdateLocations on the API, so only the fields it copies onto the leg are replaced.
 */
export function applyLocationToJobLegsInConsignmentCache(jobId: number, location: Location) {
  const { id, name, addr1, addr2, cityId, city, state, zip, areaName } = mapLocationToLegLoc(location);
  const legLocationValues: Partial<LegLocation> = { name, addr1, addr2, cityId, city, state, zip, areaName };

  /*
   * The API omits empty fields, so an empty value is removed rather than set to undefined. Otherwise the leg location has keys
   * the same location picked from the search results does not, and the form sees switching back to it as a change.
   */
  const applyValues = (legLocation: LegLocation) => {
    (Object.keys(legLocationValues) as (keyof typeof legLocationValues)[]).forEach((key) => {
      if (legLocationValues[key] === undefined) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete legLocation[key];
      } else {
        Object.assign(legLocation, { [key]: legLocationValues[key] });
      }
    });
  };

  const invalidEndpoints = consignmentSlice.util.selectInvalidatedBy(store.getState(), ['consignment']);

  return invalidEndpoints.map((currentInvalidEndpoint) => {
    type ConsignmentEndpoints = typeof consignmentSlice.endpoints;
    const consignmentEndpointName = 'fetchConsignment';
    type InvalidatedEndpointType = ConsignmentEndpoints['fetchConsignment'];

    const originalArgs = currentInvalidEndpoint.originalArgs as InvalidatedEndpointType['Types']['QueryArg'];

    return store.dispatch(
      consignmentSlice.util.updateQueryData(consignmentEndpointName, originalArgs, (draft) => {
        if (draft.jobId !== jobId) return;

        draft.consignmentLegs.forEach((leg) => {
          if (leg.pickupLocation?.id === id) applyValues(leg.pickupLocation);
          if (leg.deliveryLocation?.id === id) applyValues(leg.deliveryLocation);
        });
      })
    );
  });
}

/**
 * Patches a subset of fields on a single leg within the Consignment cache without touching the consignment
 * header values. Use this for narrow updates (e.g. puDelvReceiptSent) where a full leg is not available and the
 * broader updateLegConsignmentCache would clobber header fields with undefined.
 */
export function patchLegInConsignmentCache(legId: number, csnId: number | undefined, updates: Partial<Leg>) {
  const invalidEndpoints = consignmentSlice.util.selectInvalidatedBy(store.getState(), [{ type: 'consignment', id: csnId }]);

  return invalidEndpoints.map((currentInvalidEndpoint) => {
    type ConsignmentEndpoints = typeof consignmentSlice.endpoints;
    const consignmentEndpointName = 'fetchConsignment';
    type InvalidatedEndpointType = ConsignmentEndpoints['fetchConsignment'];

    const originalArgs = currentInvalidEndpoint.originalArgs as InvalidatedEndpointType['Types']['QueryArg'];

    return store.dispatch(
      consignmentSlice.util.updateQueryData(consignmentEndpointName, originalArgs, (draft) => {
        const leg = draft.consignmentLegs.find((leg) => leg.id === legId);
        if (leg) {
          Object.assign(leg, updates);
        }
      })
    );
  });
}
